import { loadIndexes } from '@data/indexes';
import type { CreatureVariant } from '@data/schemas';
import { html } from 'lit';

function variantsLine(variants: readonly CreatureVariant[] | undefined): unknown {
  if (!variants || variants.length === 0) return null;
  return html`<span class="creature-stat-variants">
    ${variants.map((v) => html`<span class="creature-stat-variant">${v.value} ${v.condition}</span>`)}
  </span>`;
}

export async function renderCreaturePage(id: string): Promise<unknown> {
  const idx = await loadIndexes();
  const c = idx.creatureById.get(id);
  if (!c) {
    return html`<h1 class="page-title">Creature introuvable</h1>`;
  }

  const statEntries = [
    { label: 'PV', value: c.pv, variants: undefined },
    { label: 'DEF', value: c.def, variants: c.def_variants },
    { label: 'Init', value: c.init, variants: c.init_variants },
    { label: 'ATT', value: c.att, variants: undefined },
    { label: 'DM', value: c.dm, variants: undefined },
    ...(c.rd != null ? [{ label: 'RD', value: c.rd, variants: undefined }] : []),
  ];

  return html`
    <article class="creature-detail">
      <!-- Header -->
      <header class="creature-header">
        <div class="creature-tags">
          ${c.nc != null ? html`<span class="creature-tag creature-tag-nc">NC ${c.nc}</span>` : null}
          ${c.type ? html`<span class="creature-tag">${c.type}</span>` : null}
          ${c.taille ? html`<span class="creature-tag">${c.taille}</span>` : null}
        </div>
        <h1 class="creature-name" data-morph data-morph-duration="1200">${c.nom}</h1>
      </header>

      <!-- Stat Block -->
      <section class="creature-statblock">
        <div class="creature-stats">
          ${statEntries.map(
            (s) => html`
            <div class="creature-stat">
              <span class="creature-stat-label">${s.label}</span>
              <span class="creature-stat-value">${s.value}</span>
              ${variantsLine(s.variants as readonly CreatureVariant[] | undefined)}
            </div>
          `,
          )}
        </div>
      </section>

      <!-- Characteristics -->
      ${
        c.carac
          ? html`
        <section class="creature-section">
          <h2 class="creature-section-title">Caracteristiques</h2>
          <div class="creature-caracs">
            ${Object.entries(c.carac).map(([k, v]) => {
              const val = Number(v);
              const mod = Math.floor((val - 10) / 2);
              return html`
                <div class="creature-carac">
                  <span class="creature-carac-label">${k}</span>
                  <span class="creature-carac-value">${val}</span>
                  <span class="creature-carac-mod">${mod >= 0 ? '+' : ''}${mod}</span>
                </div>
              `;
            })}
          </div>
        </section>
      `
          : null
      }

      <!-- Capacites -->
      ${
        c.capacites && c.capacites.length > 0
          ? html`
        <section class="creature-section">
          <h2 class="creature-section-title">Capacites</h2>
          <div class="creature-capacites">
            ${c.capacites.map(
              (cap, i) => html`
              <div class="creature-capacite" style="animation-delay: ${i * 40}ms">
                <div class="creature-capacite-header">
                  <span class="creature-capacite-name">${cap.nom}</span>
                  ${cap.type ? html`<span class="creature-capacite-type">${cap.type}</span>` : null}
                </div>
                <p class="creature-capacite-desc">${cap.description}</p>
              </div>
            `,
            )}
          </div>
        </section>
      `
          : null
      }

      <!-- Notes -->
      ${
        c.notes
          ? html`
        <footer class="creature-notes">
          <p>${c.notes}</p>
        </footer>
      `
          : null
      }
    </article>
  `;
}
